import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Timeline } from "../../components/game/Timeline.js";
import type { SideView } from "../../services/generated/client.js";

/**
 * Your own timeline, and — for one phase — the control that puts a card in it.
 *
 * # Two jobs, and which one it is doing depends on the room
 *
 * In **couch** mode the board is a television across the room, so your own hand
 * lives here all the time. In **remote** mode the board is already on this
 * screen, so repeating it would be a second copy of the same row — and this
 * appears only while it is a *control*.
 *
 * # It keeps the choice, and forgets it every round
 *
 * The chosen slot and the optional exact year are this component's state and
 * nowhere else's: they are a half-made decision, not game state, and the server
 * never hears about either until the placement is sent. The round screen mounts
 * this **keyed by the round number**, so a new round starts with an empty
 * choice without anybody having to remember to clear it.
 *
 * # The year is a step, not a field beside a button — phase 18, finding #15
 *
 * *"We should add extra points when they guess the year, to push them to say the
 * year."* It was already built and already paid: an exact year is worth **3**
 * (one for the slot, two for the bonus) against **1** for the slot alone, and
 * the rule is already the honest one — the bonus rides on a *correct* placement,
 * so a lucky year on a wrong slot pays nothing.
 *
 * So this was never a missing feature. It was an **invisible** one: an optional
 * text input, unlabelled as to what it was worth, on a screen a player is trying
 * to leave quickly. The stepper's answer is to ask the two questions one at a
 * time — the gap, then the year — and to say what the year is worth while
 * asking. ⚠️ It stays **optional**: making it mandatory is a rules change and
 * needs a decision, and making it findable had to be tried first.
 */
export function PlacePanel({
  side,
  cardsToWin,
  live,
  showTimeline,
  busy,
  onPlace,
}: {
  /** The viewer's side, or `null` for a spectator. */
  side: SideView | null;
  /** `GameRules.cards_to_win`, for the "how close am I" strip. */
  cardsToWin: number;
  /** Whether this player may place right now. */
  live: boolean;
  /** Whether to show the timeline at all when it is not a control. */
  showTimeline: boolean;
  busy: boolean;
  /** Where it goes, and the exact year if one was offered. */
  onPlace: (slot: number, exactYear: number | null) => void;
}) {
  const { t } = useTranslation();
  const [slot, setSlot] = useState<number | null>(null);
  const [exactYear, setExactYear] = useState("");

  return (
    <>
      {(showTimeline || live) && (
        <Timeline
          label={live ? t("round.phase.placing") : t("round.yourTimeline")}
          cards={side?.timeline ?? []}
          cardCount={side?.card_count ?? 0}
          hidden={side?.timeline_hidden ?? false}
          cardsToWin={cardsToWin}
          selectedSlot={slot}
          {...(live
            ? {
                onPlace: (chosen: number) => {
                  setSlot(chosen);
                },
              }
            : {})}
        />
      )}

      {/* **Step two, and only once step one is answered.** Before this the year
          input sat beside the timeline from the moment the phase opened,
          competing with the decision the player was actually making. */}
      {live && slot !== null && (
        <div
          data-testid="exact-year-step"
          className="flex flex-col gap-2 rounded-xl border-2 border-coin-400/40 bg-coin-400/5 p-3"
        >
          <label className="text-sm font-semibold" htmlFor="exact-year">
            {t("round.exactYear")}
          </label>
          {/* What it is worth, said where the decision is made. The whole
              finding is that nobody knew there was a bonus at all. */}
          <p className="text-xs text-coin-400">{t("round.exactYearBonus")}</p>
          <input
            id="exact-year"
            inputMode="numeric"
            placeholder="1984"
            value={exactYear}
            onChange={(event) => {
              setExactYear(event.target.value.replace(/\D/g, "").slice(0, 4));
            }}
            className="min-h-14 rounded-lg border border-white/15 bg-stage-900 px-3 text-2xl tabular-nums"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              onPlace(slot, exactYear.length === 4 ? Number(exactYear) : null);
              setExactYear("");
            }}
            className="min-h-14 rounded-lg bg-flash-500 text-lg font-semibold text-stage-950 disabled:opacity-40"
          >
            {exactYear.length === 4
              ? t("round.placeWithYear", { year: exactYear })
              : t("round.placeWithoutYear")}
          </button>
        </div>
      )}
    </>
  );
}
